import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fetchTeacherSubjectsWithStudents } from '../../services/api';
import {
  fetchUnifiedCalendarEvents,
  CALENDAR_CATEGORIES,
} from '../../services/teacherCalendar';
import './TeacherHub.css';

export default function TeacherCalendar() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const teacherId = user?.user_id || user?.id || null;

  // View mode: 'month' | 'week' | 'day'
  const [viewMode, setViewMode] = useState('month');

  // Active Anchor Date (defaults to today)
  const [currentDate, setCurrentDate] = useState(() => new Date());

  // Enabled Category Filters
  const [activeCategories, setActiveCategories] = useState(() => {
    const init = {};
    Object.keys(CALENDAR_CATEGORIES).forEach((k) => {
      init[k] = true;
    });
    return init;
  });

  // Events & Loading
  const [events, setEvents] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');

  // Selected Event Popover / Modal
  const [selectedEvent, setSelectedEvent] = useState<any>(null);

  const [expandedEventId, setExpandedEventId] = useState<any>(null);

  const handleEventClick = (eventId, e) => {
    e.stopPropagation();
    setExpandedEventId(prevId => (prevId === eventId ? null : eventId));
  };

  // Quick Action Modal (create assignment / add note on date)
  const [quickActionDate, setQuickActionDate] = useState<any>(null);
  const [selectedDay, setSelectedDay] = useState<any>(null);
  const selectDay = (iso) => setSelectedDay((current) => (current === iso ? null : iso));
  const dayKeyHandler = (iso) => (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    selectDay(iso);
  };

  // Teacher classes
  const [teacherClasses, setTeacherClasses] = useState<any>([]);

  useEffect(() => {
    async function loadClasses() {
      try {
        const subData = await fetchTeacherSubjectsWithStudents();
        const subjectsList = Array.isArray(subData) ? subData : subData?.subjects || [];
        const classes = [];
        subjectsList.forEach((sub) => {
          const sName = sub.subject_name || sub.subject || 'General';
          (sub.classes || []).forEach((c) => {
            classes.push({
              class_id: String(c.class_id || c.id),
              class_name: c.class_name || c.name || `${sName} - Class ${c.class_id}`,
              subject_name: sName,
              students: c.students || [],
            });
          });
        });
        if (classes.length > 0) setTeacherClasses(classes);
      } catch (err: any) {
        console.warn('[TeacherCalendar] Could not load teacher classes:', err);
      }
    }

    loadClasses();
  }, []);

  // Calculate range based on viewMode & currentDate
  const dateRange = useMemo(() => {
    const cur = new Date(currentDate);
    if (viewMode === 'month') {
      const start = new Date(cur.getFullYear(), cur.getMonth(), 1);
      start.setDate(start.getDate() - start.getDay()); // Start on Sunday
      const end = new Date(start);
      end.setDate(end.getDate() + 41); // 6 weeks
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
      };
    } else if (viewMode === 'week') {
      const start = new Date(cur);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
      };
    } else {
      const dStr = cur.toISOString().split('T')[0];
      return { startDate: dStr, endDate: dStr };
    }
  }, [viewMode, currentDate]);

  // Fetch events
  const loadEvents = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchUnifiedCalendarEvents({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        teacherId,
        schoolId: user?.school_id,
        teacherClasses,
      });
      setEvents(data);
    } catch (err: any) {
      console.error('Error fetching calendar events:', err);
      setError(err.message || 'Could not load calendar events.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate, teacherId, user?.school_id, teacherClasses.length]);

  // Filtered Events
  const filteredEvents = useMemo(() => {
    return events.filter((ev) => activeCategories[ev.category] !== false);
  }, [events, activeCategories]);

  // Navigate calendar anchor date
  const handlePrev = () => {
    const next = new Date(currentDate);
    if (viewMode === 'month') {
      next.setMonth(next.getMonth() - 1);
    } else if (viewMode === 'week') {
      next.setDate(next.getDate() - 7);
    } else {
      next.setDate(next.getDate() - 1);
    }
    setCurrentDate(next);
  };

  const handleNext = () => {
    const next = new Date(currentDate);
    if (viewMode === 'month') {
      next.setMonth(next.getMonth() + 1);
    } else if (viewMode === 'week') {
      next.setDate(next.getDate() + 7);
    } else {
      next.setDate(next.getDate() + 1);
    }
    setCurrentDate(next);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Toggle category
  const toggleCategory = (catKey) => {
    setActiveCategories((prev) => ({
      ...prev,
      [catKey]: !prev[catKey],
    }));
  };

  // Month grid day cells
  const monthDays = useMemo(() => {
    if (viewMode !== 'month') return [];
    const cur = new Date(currentDate);
    const start = new Date(cur.getFullYear(), cur.getMonth(), 1);
    start.setDate(start.getDate() - start.getDay()); // Sunday start
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const iso = d.toISOString().split('T')[0];
      const isCurrentMonth = d.getMonth() === cur.getMonth();
      const isToday = iso === new Date().toISOString().split('T')[0];
      const dayEvents = filteredEvents.filter((e) => e.date === iso);
      days.push({
        date: d,
        iso,
        dayNum: d.getDate(),
        isCurrentMonth,
        isToday,
        events: dayEvents,
      });
    }
    return days;
  }, [viewMode, currentDate, filteredEvents]);

  // Week days
  const weekDays = useMemo(() => {
    if (viewMode !== 'week') return [];
    const cur = new Date(currentDate);
    const start = new Date(cur);
    start.setDate(start.getDate() - start.getDay());
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const iso = d.toISOString().split('T')[0];
      const isToday = iso === new Date().toISOString().split('T')[0];
      const dayEvents = filteredEvents.filter((e) => e.date === iso);
      days.push({
        date: d,
        iso,
        dayNum: d.getDate(),
        dayName: d.toLocaleDateString(undefined, { weekday: 'short' }),
        isToday,
        events: dayEvents,
      });
    }
    return days;
  }, [viewMode, currentDate, filteredEvents]);

  // Today / Agenda items
  const agendaItems = useMemo(() => {
    const todayIso = new Date().toISOString().split('T')[0];
    const upcoming = events.filter((e) => e.date >= todayIso);
    return upcoming.slice(0, 8);
  }, [events]);

  const selectedDayEvents = useMemo(() => (
    selectedDay
      ? filteredEvents
        .filter((e) => e.date === selectedDay)
        .sort((a, b) => String(a.time || '').localeCompare(String(b.time || '')))
      : []
  ), [filteredEvents, selectedDay]);
  const selectedDayLabel = selectedDay
    ? new Date(`${selectedDay}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  // Header Title
  const headerDateString = useMemo(() => {
    if (viewMode === 'month') {
      return currentDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    } else if (viewMode === 'week') {
      const start = new Date(currentDate);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return currentDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
  }, [viewMode, currentDate]);

  return (
    <div className="th-container">
      {error && <div role="alert">{error}</div>}
      {/* Page Header */}
      <div className="th-header-row">
        <div>
          <h1 className="th-header-title">Calendar</h1>
          <p className="th-header-subtitle">
            Aggregated assessments, data points, assignment deadlines, school events, and teacher notes in one schedule.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="button"
            className="th-btn-primary"
            onClick={() => navigate('/teacher/assignments/create')}
          >
            <span>+</span> Schedule Assignment
          </button>
          <button
            type="button"
            className="th-btn-secondary"
            onClick={() => navigate('/teacher/notes')}
          >
            📝 Add Note
          </button>
        </div>
      </div>

      {/* Category Filter Pills (Section 4 Requirement) */}
      <div className="th-calendar-categories">
        {Object.entries(CALENDAR_CATEGORIES).map(([key, cat]) => {
          const isActive = activeCategories[key] !== false;
          return (
            <button
              key={key}
              type="button"
              className={`th-category-pill ${isActive ? 'active' : ''}`}
              onClick={() => toggleCategory(key)}
              style={{
                backgroundColor: isActive ? `${cat.color}15` : '#f1f5f9',
                color: isActive ? cat.color : '#94a3b8',
                borderColor: isActive ? cat.color : '#cbd5e1',
              }}
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: cat.color,
                }}
              />
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Calendar Controls Bar */}
      <div className="th-calendar-controls">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            className="th-btn-secondary th-btn-sm"
            onClick={handlePrev}
            title="Previous"
          >
            ‹
          </button>
          <button
            type="button"
            className="th-btn-secondary th-btn-sm"
            onClick={handleToday}
          >
            Today
          </button>
          <button
            type="button"
            className="th-btn-secondary th-btn-sm"
            onClick={handleNext}
            title="Next"
          >
            ›
          </button>
          <span className="th-calendar-title">
            {headerDateString}
          </span>
        </div>

        <div className="th-view-modes">
          <button
            type="button"
            className={`th-view-btn ${viewMode === 'month' ? 'active' : ''}`}
            onClick={() => setViewMode('month')}
          >
            Month
          </button>
          <button
            type="button"
            className={`th-view-btn ${viewMode === 'week' ? 'active' : ''}`}
            onClick={() => setViewMode('week')}
          >
            Week
          </button>
          <button
            type="button"
            className={`th-view-btn ${viewMode === 'day' ? 'active' : ''}`}
            onClick={() => setViewMode('day')}
          >
            Day
          </button>
        </div>
      </div>

      {/* Main Content Layout (Calendar Grid + Agenda Panel) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '20px', alignItems: 'start' }}>
        {/* Left: Interactive Calendar View */}
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              Loading schedule and events...
            </div>
          ) : viewMode === 'month' ? (
            <div className="th-month-grid">
              {/* Day Name Headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d} className="th-day-header">
                  {d}
                </div>
              ))}

              {/* 42 Month Cells */}
              {monthDays.map((cell) => (
                <div
                  key={cell.iso}
                  className={`th-calendar-cell ${!cell.isCurrentMonth ? 'other-month' : ''} ${cell.isToday ? 'is-today' : ''} ${selectedDay === cell.iso ? 'is-selected' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selectedDay === cell.iso}
                  aria-label={`Show events for ${cell.iso}`}
                  onClick={() => selectDay(cell.iso)}
                  onKeyDown={dayKeyHandler(cell.iso)}
                  title={`Click to see events on ${cell.iso}`}
                >
                  <div className="th-cell-header">
                    <span className="th-cell-date-num">{cell.dayNum}</span>
                    {cell.events.length > 0 && (
                      <span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600 }}>
                        {cell.events.length}
                      </span>
                    )}
                  </div>

                  <div className="calendar-events-container">
                    {cell.events.slice(0, 6).map((ev) => {
                      const cat = CALENDAR_CATEGORIES[ev.category] || CALENDAR_CATEGORIES.assignment;
                      const isExpanded = expandedEventId === ev.id;

                      return (
                        <div
                          key={ev.id}
                          className={`event-wrapper ${isExpanded ? 'expanded' : 'collapsed'}`}
                          onClick={(e) => handleEventClick(ev.id, e)}
                        >
                          {/* Minimal Dot View */}
                          <span
                            className="event-dot"
                            style={{ backgroundColor: cat.color }}
                            title={`${cat.label}: ${ev.title}`}
                          />

                          {/* Expanded Detail View */}
                          {isExpanded && (
                            <div className="event-expanded-card">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <strong style={{ color: '#0f172a', fontSize: '0.8rem', marginRight: '8px' }}>
                                  {ev.title}
                                </strong>
                                <span
                                  style={{
                                    backgroundColor: `${cat.color}15`,
                                    color: cat.color,
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                  }}
                                >
                                  {cat.label}
                                </span>
                              </div>
                              {ev.time && <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '4px' }}>⏰ {ev.time}</div>}
            
                              <button
                                type="button"
                                className="th-btn-secondary th-btn-sm"
                                style={{ marginTop: '8px', fontSize: '0.7rem', padding: '4px 8px', width: '100%' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedEvent(ev); // Open main modal for full view
                                  setExpandedEventId(null);
                                }}
                              >
                                Full Details →
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {cell.events.length > 6 && (
                      <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 700 }}>
                        +{cell.events.length - 6}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : viewMode === 'week' ? (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                {weekDays.map((wd) => (
                  <div
                    key={wd.iso}
                    style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      borderRight: '1px solid #e2e8f0',
                      background: wd.isToday ? '#eff6ff' : 'transparent',
                    }}
                  >
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: wd.isToday ? '#2563eb' : '#64748b' }}>
                      {wd.dayName}
                    </div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: wd.isToday ? '#2563eb' : '#0f172a' }}>
                      {wd.dayNum}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', minHeight: '460px' }}>
                {weekDays.map((wd) => (
                  <div
                    key={wd.iso}
                    style={{
                      padding: '8px',
                      borderRight: '1px solid #e2e8f0',
                      background: selectedDay === wd.iso ? '#eff6ff' : wd.isToday ? '#fbfdff' : '#ffffff',
                      boxShadow: selectedDay === wd.iso ? 'inset 0 0 0 2px #2563eb' : 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      cursor: 'pointer',
                    }}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selectedDay === wd.iso}
                    aria-label={`Show events for ${wd.iso}`}
                    onClick={() => selectDay(wd.iso)}
                    onKeyDown={dayKeyHandler(wd.iso)}
                  >
                    {wd.events.map((ev) => {
                      const cat = CALENDAR_CATEGORIES[ev.category] || CALENDAR_CATEGORIES.assignment;
                      return (
                        <div
                          key={ev.id}
                          className="th-event-pill"
                          style={{
                            backgroundColor: `${cat.color}18`,
                            color: cat.color,
                            borderLeft: `3px solid ${cat.color}`,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEvent(ev);
                          }}
                        >
                          <div style={{ fontWeight: 700 }}>{ev.title}</div>
                          {ev.time && <div style={{ fontSize: '0.65rem' }}>⏰ {ev.time}</div>}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Day View */
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#0f172a' }}>
                    {currentDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </h3>
                  <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                    {filteredEvents.length} items scheduled for this day
                  </span>
                </div>
                <button
                  type="button"
                  className="th-btn-primary th-btn-sm"
                  onClick={() => setQuickActionDate(currentDate.toISOString().split('T')[0])}
                >
                  + Add Item for Today
                </button>
              </div>

              {filteredEvents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                  No items scheduled for this date.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {filteredEvents.map((ev) => {
                    const cat = CALENDAR_CATEGORIES[ev.category] || CALENDAR_CATEGORIES.assignment;
                    return (
                      <div
                        key={ev.id}
                        className="th-note-item"
                        style={{ borderLeft: `4px solid ${cat.color}` }}
                        onClick={() => setSelectedEvent(ev)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <span
                              style={{
                                backgroundColor: `${cat.color}15`,
                                color: cat.color,
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                              }}
                            >
                              {cat.label}
                            </span>
                            <h4 style={{ margin: '6px 0 2px', fontSize: '1rem', color: '#0f172a' }}>
                              {ev.title}
                            </h4>
                            <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                              {ev.subtitle || ev.description}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="th-btn-secondary th-btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEvent(ev);
                            }}
                          >
                            Inspect
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: Agenda Panel (Section 4 Requirement) */}
        <div className="th-agenda-panel">
          {selectedDay ? (
            <section aria-label={`Events on ${selectedDayLabel}`}>
              <div className="th-agenda-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '0.98rem', color: '#0f172a' }}>{selectedDayLabel}</h3>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    {selectedDayEvents.length} {selectedDayEvents.length === 1 ? 'event' : 'events'}
                  </span>
                </div>
                <button
                  type="button"
                  aria-label="Close day events"
                  onClick={() => setSelectedDay(null)}
                  style={{ background: 'transparent', border: 'none', fontSize: '1rem', cursor: 'pointer', color: '#94a3b8' }}
                >
                  ✕
                </button>
              </div>
              <ul className="th-agenda-list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {selectedDayEvents.length === 0 ? (
                  <li style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                    No events on this day.
                  </li>
                ) : selectedDayEvents.map((item) => {
                  const cat = CALENDAR_CATEGORIES[item.category] || CALENDAR_CATEGORIES.assignment;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        className="th-agenda-item"
                        onClick={() => setSelectedEvent(item)}
                        style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', font: 'inherit', cursor: 'pointer' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: cat.color, display: 'inline-block', flexShrink: 0 }} />
                          <span className="th-agenda-item-title">{item.title}</span>
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px', paddingLeft: '14px' }}>
                          {[cat.label, item.time, item.subtitle].filter(Boolean).join(' · ')}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div style={{ padding: '12px' }}>
                <button
                  type="button"
                  className="th-btn-secondary"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => setQuickActionDate(selectedDay)}
                >
                  + Add task or note
                </button>
              </div>
            </section>
          ) : (
          <>
          <div className="th-agenda-header">
            <h3 style={{ margin: 0, fontSize: '0.98rem', color: '#0f172a' }}>
              Upcoming Agenda
            </h3>
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
              Live Platform Feed
            </span>
          </div>

          <div className="th-agenda-list">
            {agendaItems.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                No upcoming deadlines or items.
              </div>
            ) : (
              agendaItems.map((item) => {
                const cat = CALENDAR_CATEGORIES[item.category] || CALENDAR_CATEGORIES.assignment;
                return (
                  <div
                    key={item.id}
                    className="th-agenda-item"
                    onClick={() => setSelectedEvent(item)}
                  >
                    <div className="th-agenda-item-date">
                      {item.date}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: cat.color,
                          display: 'inline-block',
                          flexShrink: 0,
                        }}
                      />
                      <span className="th-agenda-item-title">
                        {item.title}
                      </span>
                    </div>
                    {item.subtitle && (
                      <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px', paddingLeft: '14px' }}>
                        {item.subtitle}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          </>
          )}
        </div>
      </div>

      {/* Interactive Detail Popover / Modal (Section 4 Requirement) */}
      {selectedEvent && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.4)',
            backdropFilter: 'blur(2px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '20px',
          }}
          onClick={() => setSelectedEvent(null)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '14px',
              maxWidth: '500px',
              width: '100%',
              padding: '24px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
              <div>
                <span
                  style={{
                    backgroundColor: `${CALENDAR_CATEGORIES[selectedEvent.category]?.color || '#2563eb'}15`,
                    color: CALENDAR_CATEGORIES[selectedEvent.category]?.color || '#2563eb',
                    padding: '3px 10px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                  }}
                >
                  {CALENDAR_CATEGORIES[selectedEvent.category]?.label || 'Event'}
                </span>
                <h2 style={{ margin: '8px 0 2px', fontSize: '1.2rem', color: '#0f172a' }}>
                  {selectedEvent.title}
                </h2>
                <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
                  📅 {selectedEvent.date} {selectedEvent.time ? `• ⏰ ${selectedEvent.time}` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedEvent(null)}
                style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#94a3b8' }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ margin: '16px 0', fontSize: '0.88rem', color: '#334155', lineHeight: 1.6 }}>
              {selectedEvent.subtitle && (
                <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '6px' }}>
                  {selectedEvent.subtitle}
                </div>
              )}
              {selectedEvent.description && (
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {selectedEvent.description}
                </p>
              )}
            </div>

            {/* Action Buttons (Direct Links to Tools) */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                type="button"
                className="th-btn-secondary"
                onClick={() => setSelectedEvent(null)}
              >
                Close
              </button>

              {/* Dynamic Action Link based on Category */}
              {selectedEvent.actionLink && (
                <button
                  type="button"
                  className="th-btn-primary"
                  onClick={() => {
                    navigate(selectedEvent.actionLink, { state: selectedEvent.actionState });
                  }}
                >
                  {selectedEvent.actionLabel || 'View Details'} →
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Action Modal on Date Click */}
      {quickActionDate && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.4)',
            backdropFilter: 'blur(2px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '20px',
          }}
          onClick={() => setQuickActionDate(null)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '14px',
              maxWidth: '420px',
              width: '100%',
              padding: '24px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 6px', fontSize: '1.1rem', color: '#0f172a' }}>
              Actions for {quickActionDate}
            </h3>
            <p style={{ margin: '0 0 18px', fontSize: '0.82rem', color: '#64748b' }}>
              Schedule an assignment, record a classroom observation, or view existing items for this date.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                type="button"
                className="th-btn-primary"
                style={{ justifyContent: 'center' }}
                onClick={() => {
                  navigate('/teacher/assignments/create', { state: { defaultDueDate: quickActionDate } });
                }}
              >
                📝 Create Assignment / Assessment
              </button>

              <button
                type="button"
                className="th-btn-secondary"
                style={{ justifyContent: 'center' }}
                onClick={() => {
                  navigate('/teacher/notes', { state: { selectedDate: quickActionDate } });
                }}
              >
                ✍️ Add Note on {quickActionDate}
              </button>

              <button
                type="button"
                className="th-btn-secondary"
                style={{ justifyContent: 'center', color: '#64748b' }}
                onClick={() => setQuickActionDate(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
